import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BookingConfirmation,
  BookingWizard,
  AppointmentsList,
  AppHeader,
} from "../components/customer";
import { useAppointmentBooking } from "../hooks/useAppointmentBooking";
import { Service, Professional, BookingData, Company } from "../types";
import { publicApi } from "../services";
import { ThemeProvider, useTheme } from "../contexts/ThemeContext";

const ThemedCustomerBooking: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setCompany: setThemeCompany } = useTheme();

  // Determine current view from route
  const isAppointmentsView = location.pathname.includes("/appointments");

  const [phoneNumber, setPhoneNumber] = useState("");
  const [company, setCompany] = useState<Company | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedSubServices, setSelectedSubServices] = useState<number[]>([]);
  const [selectedProfessional, setSelectedProfessional] =
    useState<Professional | null>(null);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  // const [professionalsLoading, setProfessionalsLoading] = useState(false); // Currently unused
  const {
    timeSlots,
    loading,
    error,
    selectedDate,
    selectedTimeSlot,
    bookingResult,
    setSelectedDate,
    setSelectedTimeSlot,
    fetchTimeSlots,
    bookAppointment,
    resetBooking,
  } = useAppointmentBooking();

  // Fetch company data on mount
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await publicApi.getCompany();
        if (response.success && response.data) {
          setCompany(response.data);
          setThemeCompany(response.data); // Set theme based on company
          setProfessionals(response.data.professionals || []);
        }
      } catch (error) {
        console.error("Failed to fetch company:", error);
      }
    };

    fetchCompany();
  }, [setThemeCompany]);

  // Extract phone number from query parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const phone = urlParams.get("phoneNumber") || urlParams.get("phone") || "";
    setPhoneNumber(phone);
  }, []);

  // Fetch time slots when date or service changes (only for non-barbershop)
  useEffect(() => {
    if (selectedDate && selectedService) {
      // For barbershop, we need a professional selected first
      // For other categories (like car), we can use a default professional or the selected one
      const professionalId = selectedProfessional?.id || 1; // Default to professional ID 1

      fetchTimeSlots(
        selectedDate,
        selectedService.id,
        professionalId,
        company
      );
      setSelectedTimeSlot(null); // Reset selected time slot when date or service changes
    }
  }, [
    selectedDate,
    selectedService,
    selectedProfessional,
    company,
    fetchTimeSlots,
    setSelectedTimeSlot,
  ]);

  // Wrapper for bookAppointment to include company info
  const handleBookAppointment = async (bookingData: BookingData) => {
    return bookAppointment(bookingData, company);
  };

  const handleNewBooking = () => {
    resetBooking();
    setSelectedService(null);
    setSelectedSubServices([]);
    setSelectedProfessional(null);
    setSelectedDate(""); // Reset to empty string to show date picker
    navigate(`/client`);
  };

  // Handle service change and reset subservices
  const handleServiceChange = (service: Service) => {
    setSelectedService(service);
    setSelectedSubServices([]); // Reset subservices when service changes
  };

  // Handle subservices change
  const handleSubServicesChange = (subServiceIds: number[]) => {
    setSelectedSubServices(subServiceIds);
  };

  const handleViewAppointments = () => {
    navigate(`/client/appointments`);
  };

  const handleBackToBooking = () => {
    navigate(`/client`);
  };

  // Show appointments list first (check route before booking result)
  if (isAppointmentsView) {
    return (
      <div className="app">
        <AppointmentsList
          phoneNumber={phoneNumber}
          onBackToBooking={handleBackToBooking}
        />
      </div>
    );
  }

  // If booking is confirmed, show confirmation
  if (bookingResult) {
    return (
      <div className="app">
        <BookingConfirmation
          appointment={bookingResult}
          onNewBooking={handleNewBooking}
          onViewAppointments={handleViewAppointments}
        />
      </div>
    );
  }

  // Show booking flow (default)
  return (
    <div>
      <AppHeader
        onViewAppointments={handleViewAppointments}
        company={company}
      />
      <main>
        <BookingWizard
          company={company}
          selectedDate={selectedDate}
          selectedService={selectedService}
          selectedSubServices={selectedSubServices}
          selectedProfessional={selectedProfessional}
          selectedTimeSlot={selectedTimeSlot}
          timeSlots={timeSlots}
          professionals={professionals}
          loading={loading}
          professionalsLoading={false}
          error={error}
          phoneNumber={phoneNumber}
          onDateChange={setSelectedDate}
          onServiceChange={handleServiceChange}
          onSubServicesChange={handleSubServicesChange}
          onProfessionalChange={setSelectedProfessional}
          onTimeSlotSelect={setSelectedTimeSlot}
          bookAppointment={handleBookAppointment}
        />
      </main>
    </div>
  );
};

// Main component with ThemeProvider wrapper
export const CustomerBooking: React.FC = () => {
  return (
    <ThemeProvider>
      <ThemedCustomerBooking />
    </ThemeProvider>
  );
};
